import {config} from './config';
import {ConfirmPaymentParams} from './paymentModal';
import {
  CalculateFinancingRequest,
  CalculateFinancingResponse,
  CreateApplePaySessionRequest,
  CreatePaymentRequest,
  CreateTokenRequest,
  CreateTokenResponse,
  GetPaymentMethodsRequest,
  GetPaymentMethodsResponse,
  PaymentResult,
  SendPaymentReceiptRequest,
  ValidateBizumPhoneRequest
} from './types';
import {getClientEnv, stringify, withError} from './utils';

type RequestParams = {
  method?: string;
  headers?: any;
  body?: any;
};
const request = async (url: string, {method = 'GET', headers, body}: RequestParams = {}) => {
  if (method === 'POST') headers = {'Content-Type': 'application/json', ...headers};
  const res = await fetch(`${config.apiUrl}${url}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  const [, result] = await withError(res.json());
  if (!res.ok) throw result || new Error(res.statusText);
  return result;
};

const pmCache: Record<string, Promise<GetPaymentMethodsResponse>> = {};

export const getPaymentMethods = async ({
  accountId,
  paymentId
}: GetPaymentMethodsRequest): Promise<GetPaymentMethodsResponse> => {
  let params: GetPaymentMethodsRequest;
  if (paymentId) params = {paymentId};
  else if (accountId) params = {accountId};
  else throw new Error('You need to provide paymentId or accountId');
  const query = stringify(params);
  if (pmCache[query]) return pmCache[query];
  const promise = request(`/payment-methods?${query}`);
  pmCache[query] = promise;
  return promise;
};

export const createToken = async ({
  paymentMethod,
  paymentId,
  sessionId,
  amount,
  currency,
  accountId,
  transactionType
}: CreateTokenRequest): Promise<CreateTokenResponse> => {
  let body: CreateTokenRequest;
  if (paymentId) {
    body = {paymentId, paymentMethod};
  } else if (accountId) {
    body = {accountId, paymentMethod, sessionId, amount, currency, transactionType};
  } else throw new Error('You need to provide paymentId or accountId');
  return request('/tokens', {
    method: 'POST',
    headers: {'Client-Env': getClientEnv()},
    body: body
  });
};

export const getPayment = async (paymentId: string): Promise<PaymentResult> => {
  return request(`/payments/${paymentId}/client-get`);
};

export const confirmPayment = async ({paymentId, ...body}: ConfirmPaymentParams): Promise<PaymentResult> => {
  return request(`/payments/${paymentId}/client-confirm`, {
    method: 'POST',
    headers: {'Client-Env': getClientEnv()},
    body
  });
};

export const createPayment = async ({signature, ...body}: CreatePaymentRequest): Promise<PaymentResult> => {
  return request('/client-payments', {
    method: 'POST',
    headers: {
      'Client-Env': getClientEnv(),
      'Client-Signature': signature
    },
    body
  });
};

export const sendPaymentReceipt = async ({
  paymentId,
  customerEmail,
  signature,
  language
}: SendPaymentReceiptRequest): Promise<PaymentResult> => {
  return request(`/payments/${paymentId}/client-receipt`, {
    method: 'POST',
    body: {customerEmail, signature, language}
  });
};

export const createApplePaySession = async (body: CreateApplePaySessionRequest) => {
  return request(`/apple-pay/sessions`, {method: 'POST', body});
};

export const calculateFinancing = async (req: CalculateFinancingRequest): Promise<CalculateFinancingResponse> => {
  return request(`/cofidis/calculate-financing`, {method: 'POST', body: req});
};

export const validateBizumPhone = async (req: ValidateBizumPhoneRequest): Promise<{isValid: boolean}> => {
  return request(`/bizum/validate-phone`, {method: 'POST', body: req});
};
